// Tratamento da foto do produto para o catálogo.
//
// Tudo acontece aqui no navegador, e a imagem tratada é o que vai pro PDF —
// assim o que a gerente vê na tela é exatamente o que sai impresso. Se o
// recorte fosse refeito no servidor, qualquer diferença de versão mudaria o
// resultado sem ela perceber.

import { mascaraIA } from './recorteIA';

const LADO_MAX = 560;   // leve pro WhatsApp e ainda nítido no tamanho do card
const LADO_TRABALHO = 1000;   // o recorte trabalha com pelo menos isso (detalhe da borda)

// Quanto a cor pode se afastar do fundo e ainda contar como fundo.
const TOL_CHEIA = 16;   // até aqui é fundo puro (some de vez)
const TOL_BORDA = 62;   // daqui até lá é borda: some aos poucos (anti-serrilhado)
// Recorte inteligente (recorteIA.js): a IA dá a chance de cada pixel ser produto.
const TOL_PURO = 10;    // a cor do fundo mesmo, só com o ruído do JPEG
const IA_CERTA = 0.9;   // daqui pra cima a IA tem certeza que é produto: não apaga
const IA_NADA = 0.5;    // daqui pra baixo a IA não opina e vale só a cor
// Vão fechado (dentro da alça do balde, entre os degraus da escada): o fundo
// aparece mas não se liga à borda. Só sai se for a cor exata do fundo, a IA não
// vir produto ali e for grande — brilho estourado no produto não fura.
const TOL_BURACO = 4;
const BURACO_MIN = 0.002;   // fração da foto
// Produto branco no fundo branco (o painel de LED na frente da caixa): a IA vê
// a caixa e acha que o painel é fundo. Aí ela olha de novo com a caixa apagada,
// e a área clara que uma das olhadas vê como produto fica sólida.
const VOTO = 0.6;           // fração da área clara que a IA precisa ver como produto
const SEM_DONO = 0.04;      // fração da foto que não é fundo nem a IA reconhece: olha de novo
// Área que só a 2ª olhada vê precisa de confiança média: sombra no chão sai com
// ~0,6, produto de verdade passa de 0,8.
const NOVA_CONFIANCA = 0.75;
const CERCADO = 0.25;       // sobra cercada de produto: no máx. isso da volta encosta no fundo
const RESGATE = 0.03;       // até onde (fração do lado) a beira do produto branco é devolvida
// Fração da borda que precisa ser da mesma cor pra existir "fundo" pra recortar.
const BORDA_UNIFORME = 0.72;

// Classes cujo produto É uma superfície: a foto mostra a textura, não um objeto
// sobre fundo. No catálogo de 2025 essas sempre preenchem o quadro, e recortar
// destrói a peça — o fundo claro da foto é a própria cerâmica.
const CLASSES_SUPERFICIE = /PISO|PORCELANATO|GRESALATO|REVESTIMENTO|PASTILHA|AZULEJO|CERAMICA|TELHA/i;

/** A foto desta classe deve preencher o quadro em vez de ser recortada? */
export function ehSuperficie(classe) {
  return CLASSES_SUPERFICIE.test(String(classe || ''));
}

/** Carrega uma URL (ou data URI) num <img> já decodificado. */
export function carregarImagem(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Não consegui carregar a imagem.'));
    img.src = src;
  });
}

const distancia = (px, p, fundo) => Math.max(
  Math.abs(px[p * 4] - fundo[0]),
  Math.abs(px[p * 4 + 1] - fundo[1]),
  Math.abs(px[p * 4 + 2] - fundo[2]),
);

/**
 * Olha a moldura de 1px da foto e responde duas coisas: qual é a cor do fundo
 * e se existe mesmo um fundo liso.
 *
 * Isso é o que separa foto de estúdio (produto sobre fundo liso) de foto de
 * textura, tipo porcelanato, que ocupa a imagem inteira. Sem essa checagem o
 * recorte entrava numa foto de mármore e comia todas as partes claras, deixando
 * buracos pretos no meio da pedra.
 */
function amostrasDaBorda(W, H) {
  const passo = Math.max(1, Math.round((W + H) / 400));
  const amostras = [];
  for (let x = 0; x < W; x += passo) { amostras.push(x); amostras.push((H - 1) * W + x); }
  for (let y = 0; y < H; y += passo) { amostras.push(y * W); amostras.push(y * W + W - 1); }
  return amostras;
}

function analisarBorda(px, W, H) {
  const amostras = amostrasDaBorda(W, H);

  const mediana = (canal) => {
    const v = amostras.map(p => px[p * 4 + canal]).sort((a, b) => a - b);
    return v[v.length >> 1];
  };
  const fundo = [mediana(0), mediana(1), mediana(2)];
  const parecidos = amostras.filter(p => distancia(px, p, fundo) < 30).length;
  return { fundo, uniformidade: parecidos / amostras.length };
}

/**
 * Pixels ligados à moldura da foto (ou a `extras`) por um caminho em que
 * `pode(p)` vale.
 */
function inundarDaBorda(W, H, pode, extras = []) {
  const visto = new Uint8Array(W * H);
  const fila = [];
  const semear = (p) => { if (!visto[p] && pode(p)) { visto[p] = 1; fila.push(p); } };
  for (let x = 0; x < W; x++) { semear(x); semear((H - 1) * W + x); }
  for (let y = 0; y < H; y++) { semear(y * W); semear(y * W + W - 1); }
  for (const p of extras) semear(p);
  while (fila.length) {
    const p = fila.pop();
    const x = p % W;
    if (x > 0) semear(p - 1);
    if (x < W - 1) semear(p + 1);
    if (p >= W) semear(p - W);
    if (p < W * (H - 1)) semear(p + W);
  }
  return visto;
}

/**
 * Vizinhos de p (4 lados); -1 fora da foto. Devolve sempre o mesmo vetor (são
 * milhões de chamadas por foto): use antes de chamar de novo.
 */
const VIZ = new Int32Array(4);
const vizinhos = (p, W, N) => {
  const x = p % W;
  VIZ[0] = x > 0 ? p - 1 : -1;
  VIZ[1] = x < W - 1 ? p + 1 : -1;
  VIZ[2] = p >= W ? p - W : -1;
  VIZ[3] = p + W < N ? p + W : -1;
  return VIZ;
};

/** Manchas ligadas de pixels em que `dentro(p)` vale, com pelo menos `minimo` pixels. */
function manchas(W, H, dentro, minimo) {
  const N = W * H;
  const visto = new Uint8Array(N);
  const lista = [];
  for (let p0 = 0; p0 < N; p0++) {
    if (visto[p0] || !dentro(p0)) continue;
    const m = [p0];
    visto[p0] = 1;
    for (let i = 0; i < m.length; i++) {
      for (const q of vizinhos(m[i], W, N)) if (q >= 0 && !visto[q] && dentro(q)) { visto[q] = 1; m.push(q); }
    }
    if (m.length >= minimo) lista.push(m);
  }
  return lista;
}

/** Fração da mancha que a IA vê como produto. */
function voto(mancha, ia) {
  let n = 0;
  for (const p of mancha) if (ia[p] >= 128) n++;
  return n / mancha.length;
}

/** Cresce a máscara `vezes` pixels pros lados. */
function dilatar(m, W, H, vezes) {
  const N = W * H;
  for (let k = 0; k < vezes; k++) {
    const antes = m.slice();
    for (let p = 0; p < N; p++) {
      if (antes[p]) continue;
      for (const q of vizinhos(p, W, N)) if (q >= 0 && antes[q]) { m[p] = 1; break; }
    }
  }
  return m;
}

/**
 * A foto com o que a IA já achou apagado (pintado da cor do fundo), pra ela
 * olhar de novo e enxergar o produto que ficou atrás da primeira resposta.
 */
function semOQueAchou(px, ia, fundo, W, H) {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  const im = ctx.createImageData(W, H);
  for (let p = 0; p < W * H; p++) {
    const k = p * 4, m = ia[p] / 255;
    for (let i = 0; i < 3; i++) im.data[k + i] = px[k + i] * (1 - m) + fundo[i] * m;
    im.data[k + 3] = 255;
  }
  ctx.putImageData(im, 0, 0);
  return c;
}

/**
 * Com o produto apagado, a IA às vezes "vê" o que sobrou em volta: a sombra no
 * chão, o vão de dentro da curva da torneira. Zera em `ia2` (no lugar) toda área
 * nova que não tiver confiança, ou que for o fundo branco ligado à borda.
 */
function filtrarSegundaOlhada(W, H, ia1, ia2, branco) {
  for (const s of manchas(W, H, p => ia2[p] >= 128 && ia1[p] < 128, 1)) {
    let soma = 0, noBranco = 0;
    for (const p of s) { soma += ia2[p]; if (branco[p]) noBranco++; }
    if (soma < NOVA_CONFIANCA * 255 * s.length || noBranco > s.length / 2) for (const p of s) ia2[p] = 0;
  }
  for (let p = 0; p < W * H; p++) if (branco[p]) ia2[p] = 0;
}

/** 0 a 1 conforme a chance da IA (0–255) vai de `de` a `ate`. */
const faixa = (v, de, ate) => Math.min(1, Math.max(0, (v / 255 - de) / (ate - de)));

/**
 * Com a IA: o que fica sólido sem passar pela transparência gradual, e a beira
 * de produto branco que volta do fundo puro (`resgate`). Mexe em
 * `medida.fundoPuro`, tirando dele o que foi resgatado.
 */
function produtoSolido(W, H, medida, ia, olhadas, branco) {
  const N = W * H;
  const { alcance, fundoPuro, claras } = medida;
  const solido = new Uint8Array(N), duvida = new Uint8Array(N);
  let algumaClara = false;
  for (const c of claras) {
    // Fundo branco que a IA "embaçou" (torneira de parede: ela vê produto em
    // meia foto) e só não foi apagado porque ela barrou: é fundo.
    let noBranco = 0;
    for (const p of c) if (branco[p]) noBranco++;
    if (noBranco > c.length / 2) { for (const p of c) fundoPuro[p] = 1; continue; }
    // Área clara é produto quando alguma das olhadas da IA vê isso com folga. O
    // vão do assento (meio a meio nas duas) fica duvidoso: segue o alfa gradual.
    const ok = Math.max(...olhadas.map(o => voto(c, o))) >= VOTO;
    if (ok) algumaClara = true;
    for (const p of c) (ok ? solido : duvida)[p] = 1;
  }

  // Faixa de 2 px junto do fundo (e da área clara duvidosa): é borda, fica no
  // alfa gradual. Pra dentro dela, o que a IA acha mais produto que fundo e
  // encosta na área clara que virou produto também é sólido (as manchas no
  // painel). Solto no fundo não: em foto em que a IA "embaça" o fundo (torneira
  // de parede), virava borrão branco.
  const beira = fundoPuro.slice();
  for (let p = 0; p < N; p++) if (duvida[p]) beira[p] = 1;
  dilatar(beira, W, H, 2);
  const crescer = [];
  for (let p = 0; p < N; p++) if (solido[p]) crescer.push(p);
  while (crescer.length) {
    for (const q of vizinhos(crescer.pop(), W, N)) {
      if (q < 0 || solido[q] || !alcance[q] || beira[q] || ia[q] < 128) continue;
      solido[q] = 1;
      crescer.push(q);
    }
  }

  // Sobra cercada de produto (a lateral do painel, entre ele e a caixa): sólida.
  // Sombra no chão encosta no fundo e não entra.
  const certa = IA_CERTA * 255;
  const produto = p => !alcance[p] || ia[p] >= certa || solido[p];
  const marca = new Uint8Array(N);
  for (const s of manchas(W, H, p => alcance[p] && !beira[p] && !produto(p), 30)) {
    for (const p of s) marca[p] = 1;
    let noFundo = 0, noProduto = 0;
    for (const p of s) {
      for (const q of vizinhos(p, W, N)) {
        if (q < 0 || marca[q]) continue;
        if (produto(q)) noProduto++; else noFundo++;
      }
    }
    for (const p of s) marca[p] = 0;
    if (noFundo < CERCADO * (noFundo + noProduto)) for (const p of s) solido[p] = 1;
  }

  // Pela beira de produto branco o fundo puro entra em degraus (os blocos do
  // JPEG). Dali até RESGATE do lado da foto, o que a IA acha produto volta.
  const resgate = new Uint8Array(N);
  if (algumaClara) {
    const limite = 0.4 * 255, R = Math.round(RESGATE * Math.max(W, H));
    const visto = solido.slice();
    let fila = [];
    for (let p = 0; p < N; p++) if (solido[p]) fila.push(p);
    for (let k = 0; k < R && fila.length; k++) {
      const prox = [];
      for (const p of fila) {
        for (const q of vizinhos(p, W, N)) {
          if (q < 0 || visto[q] || ia[q] < limite || branco[q]) continue;
          visto[q] = 1; resgate[q] = 1; fundoPuro[q] = 0;
          prox.push(q);
        }
      }
      fila = prox;
    }
  }
  return { solido, resgate };
}

/**
 * Apaga o fundo a partir das bordas. A cor do fundo é medida na própria foto
 * (nem todo catálogo usa branco puro — tem cinza claro, tem azulado), e a
 * transparência vai saindo aos poucos na faixa de borda, pra não deixar aquele
 * contorno branco serrilhado em volta do produto.
 *
 * Só caminha a partir das bordas, então o branco do meio do produto (uma
 * geladeira, um vaso) continua lá.
 *
 * Com `src`, entra o recorte inteligente: a cor sozinha não separa produto
 * branco de fundo branco (o assento ficava transparente, cinza no fundo preto
 * do pôster). Onde a IA garante que é produto, fica sólido; o fundo puro ligado
 * à borda some mesmo que a IA hesite, e os vãos fechados (alça, degraus) saem
 * também. Sem a IA (falhou, ou foto de piso), é o recorte por cor de sempre.
 *
 * A IA vê um produto por vez: no painel de LED na frente da caixa, ela pegava
 * só a caixa e o painel branco sumia. Quando sobra área clara sem dono, ela
 * olha de novo com o que já achou apagado (ver VOTO e produtoSolido).
 */
async function recortarFundo(img, src) {
  const W = img.naturalWidth || img.width, H = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);

  let d;
  try { d = ctx.getImageData(0, 0, W, H); } catch { return { canvas, apagados: 0, semFundo: true }; }
  const px = d.data;

  // PNG que já veio recortado (do Canva, por exemplo): a moldura é transparente.
  // Recortar de novo lia esse transparente como preto e apagava as partes
  // escuras do produto — era o preto "ficando transparente".
  const borda = amostrasDaBorda(W, H);
  if (borda.filter(p => px[p * 4 + 3] < 16).length >= borda.length * BORDA_UNIFORME) {
    return { canvas, apagados: 0, semFundo: false, jaRecortada: true };
  }

  const { fundo, uniformidade } = analisarBorda(px, W, H);
  // Borda bagunçada = a foto é o próprio conteúdo (textura, ambiente). Não mexe.
  if (uniformidade < BORDA_UNIFORME) return { canvas, apagados: 0, semFundo: true };

  const N = W * H;
  const dist = new Uint8Array(N);
  for (let p = 0; p < N; p++) dist[p] = distancia(px, p, fundo);
  const pertoDoFundo = p => dist[p] < TOL_BORDA;
  const minimo = Math.max(50, BURACO_MIN * N);
  const alcanceCor = inundarDaBorda(W, H, pertoDoFundo);   // até onde o fundo chega pela cor

  // Com a IA: os vãos fechados (o fundo chega neles também), o fundo puro e as
  // áreas claras que ele não alcança (produto branco ou vão com sombra).
  const medir = (ia) => {
    if (!ia) return { alcance: alcanceCor };
    const vaos = [];
    for (const m of manchas(W, H, p => !alcanceCor[p] && dist[p] <= TOL_BURACO && ia[p] < 128, minimo)) {
      for (const p of m) vaos.push(p);
    }
    const alcance = vaos.length ? inundarDaBorda(W, H, pertoDoFundo, vaos) : alcanceCor;
    const fundoPuro = inundarDaBorda(W, H, p => dist[p] <= TOL_PURO && ia[p] < IA_CERTA * 255, vaos);
    const claras = manchas(W, H, p => dist[p] <= TOL_PURO && !fundoPuro[p], minimo);
    return { alcance, fundoPuro, claras };
  };

  let ia = src ? await mascaraIA(img, src) : null;   // 0–255 por pixel, ou null
  let medida = medir(ia);
  const olhadas = [ia];
  // Fundo branco puro ligado à borda só pela cor, sem perguntar pra IA. A 2ª
  // olhada, as áreas claras e o resgate não podem chamar isso de produto.
  const branco = ia && inundarDaBorda(W, H, p => dist[p] <= TOL_BURACO);
  if (ia) {
    // Segunda olhada quando sobra área clara que a IA não chamou de produto, ou
    // muita coisa que nem é fundo nem ela reconhece (a figura na caixa do painel).
    let semDono = 0;
    for (let p = 0; p < N; p++) if (!medida.fundoPuro[p] && ia[p] < 128 && dist[p] > TOL_PURO) semDono++;
    if (medida.claras.some(c => voto(c, ia) < 0.5) || semDono > SEM_DONO * N) {
      const ia2 = await mascaraIA(semOQueAchou(px, ia, fundo, W, H), src, 2);
      if (ia2) {
        filtrarSegundaOlhada(W, H, ia, ia2, branco);
        olhadas.push(ia2);
        const junta = new Uint8Array(N);
        for (let p = 0; p < N; p++) junta[p] = Math.max(ia[p], ia2[p]);
        ia = junta;
        medida = medir(ia);
      }
    }
  }
  const { alcance, fundoPuro } = medida;
  const { solido, resgate } = ia ? produtoSolido(W, H, medida, ia, olhadas, branco) : {};

  let apagados = 0;
  for (let p = 0; p < N; p++) {
    if (!alcance[p]) continue;          // o fundo não chega aqui: é produto
    if (solido && solido[p]) continue;  // a IA (e o que cerca) garante: é produto
    // Perto do fundo some de vez; na faixa de borda a transparência é gradual.
    let a = dist[p] <= TOL_CHEIA ? 0 : (dist[p] - TOL_CHEIA) / (TOL_BORDA - TOL_CHEIA);
    if (ia) {
      if (fundoPuro[p]) a = 0;
      else a = Math.max(a, faixa(ia[p], IA_NADA, IA_CERTA));
      // Beira de produto branco: vale o contorno da IA, mais justo.
      if (resgate[p]) a = Math.max(a, faixa(ia[p], 0.4, 0.6));
    }
    // Pixel de borda é mistura do produto com o fundo (C = aF + (1-a)B): tira o
    // fundo da cor, senão fica um contorno claro em volta no fundo escuro.
    if (a > 0.02 && a < 0.98) {
      for (let c = 0; c < 3; c++) px[p * 4 + c] = (px[p * 4 + c] - (1 - a) * fundo[c]) / a;
    }
    px[p * 4 + 3] = Math.round(a * 255);
    if (a === 0) apagados++;
  }
  ctx.putImageData(d, 0, 0);
  // Quanto da foto a IA vê como produto (separa produto fino de foto de piso).
  let produtoIA = 0;
  if (ia) for (let p = 0; p < N; p++) if (ia[p] >= 128) produtoIA++;
  return { canvas, apagados: apagados / N, semFundo: false, produtoIA: produtoIA / N };
}

/** Reduz mantendo a proporção, só se passar do limite. */
function reduzir(origem, lado = LADO_MAX) {
  const { width: W, height: H } = origem;
  const escala = Math.min(1, lado / Math.max(W, H));
  if (escala === 1) return origem;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(W * escala);
  canvas.height = Math.round(H * escala);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(origem, 0, 0, canvas.width, canvas.height);
  return canvas;
}

// ---------------------------------------------------------------------------
// "Dois pisos": a peça duplicada, uma atrás (em cima, à esquerda) e outra na
// frente — o jeito que a Aline sempre monta foto de piso. Medido na foto de
// referência dela: a da frente desloca 23% pra direita e 13% pra baixo e é 3%
// menor.
// ---------------------------------------------------------------------------
const PISO_DX = 0.23, PISO_DY = 0.13, PISO_FRENTE = 0.97;

/**
 * Corta a margem de fundo em volta da peça. Devolve o canvas só com a peça e
 * se ela já parece ser uma montagem de duas peças (aí não duplica de novo).
 */
function aparar(img) {
  const W = img.naturalWidth, H = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const px = ctx.getImageData(0, 0, W, H).data;
  const inteira = { peca: c, jaMontada: false };
  const { fundo, uniformidade } = analisarBorda(px, W, H);
  // Borda bagunçada = a foto inteira já é a peça.
  if (uniformidade < BORDA_UNIFORME) return inteira;

  const ehFundo = (x, y) => distancia(px, y * W + x, fundo) < 22;
  const linhaVazia = (y) => { for (let x = 0; x < W; x += 2) if (!ehFundo(x, y)) return false; return true; };
  const colunaVazia = (x) => { for (let y = 0; y < H; y += 2) if (!ehFundo(x, y)) return false; return true; };
  let y0 = 0, y1 = H - 1, x0 = 0, x1 = W - 1;
  while (y0 < y1 && linhaVazia(y0)) y0++;
  while (y1 > y0 && linhaVazia(y1)) y1--;
  while (x0 < x1 && colunaVazia(x0)) x0++;
  while (x1 > x0 && colunaVazia(x1)) x1--;

  // Piso liso fotografado de ponta a ponta: a borda tem a mesma cor do meio, e
  // a "margem de fundo" engoliria a peça inteira. Sobrou pouco = o fundo era a
  // própria peça — usa a foto toda.
  if ((x1 - x0 + 1) * (y1 - y0 + 1) < W * H * 0.35) return inteira;

  // Se dois cantos opostos da caixa são fundo, a foto já vem com as duas peças
  // escalonadas (é o caso de foto que a própria Aline montou e subiu no ERP).
  // Exige os DOIS lados: dois cantos vazios e os outros dois com peça. Só
  // "cantos vazios" não basta — num piso liso os quatro cantos têm a cor do
  // "fundo", e ele era tratado como montagem e apagado inteiro.
  const m = Math.round(Math.min(x1 - x0, y1 - y0) * 0.04);
  const ce = ehFundo(x0 + m, y0 + m), cd = ehFundo(x1 - m, y0 + m);
  const be = ehFundo(x0 + m, y1 - m), bd = ehFundo(x1 - m, y1 - m);
  const jaMontada = (cd && be && !ce && !bd) || (ce && bd && !cd && !be);

  const peca = document.createElement('canvas');
  peca.width = x1 - x0 + 1; peca.height = y1 - y0 + 1;
  peca.getContext('2d').drawImage(c, x0, y0, peca.width, peca.height, 0, 0, peca.width, peca.height);
  return { peca, jaMontada };
}

async function doisPisos(img) {
  const { peca, jaMontada } = aparar(img);
  // Foto que já tem as duas peças: só tira o fundo, senão viram quatro. (Sem a
  // IA: piso é superfície, não objeto, e ela não ajuda aqui.)
  if (jaMontada) return (await recortarFundo(img)).canvas;

  const w = peca.width, h = peca.height;
  const dx = Math.round(w * PISO_DX), dy = Math.round(h * PISO_DY);
  const wf = Math.round(w * PISO_FRENTE), hf = Math.round(h * PISO_FRENTE);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(w, dx + wf);
  canvas.height = Math.max(h, dy + hf);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(peca, 0, 0, w, h);                        // a de trás
  // Sombra suave da peça da frente sobre a de trás: em piso de cor parecida é
  // ela que separa uma da outra.
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.45)';
  ctx.shadowBlur = Math.round(w * 0.035);
  ctx.shadowOffsetX = -Math.round(w * 0.008);
  ctx.shadowOffsetY = -Math.round(h * 0.008);
  ctx.drawImage(peca, dx, dy, wf, hf);                    // a da frente
  ctx.restore();
  return canvas;
}

/**
 * Estilos de foto que a gerente escolhe no editor:
 *   'dois-pisos' — peça duplicada e escalonada (padrão de piso/porcelanato)
 *   'preencher'  — a foto cobre o quadro inteiro (ambiente, textura)
 *   'recorte'    — tira o fundo e o produto flutua (padrão do resto)
 *   'original'   — a foto como veio, sem mexer
 */
export const ESTILOS = [
  { valor: 'dois-pisos', rotulo: 'Dois pisos' },
  { valor: 'recorte', rotulo: 'Recortado' },
  { valor: 'preencher', rotulo: 'Preencher o quadro' },
  { valor: 'original', rotulo: 'Foto original' },
];

/** Estilo inicial de um produto, pela classe dele. */
export function estiloPadrao(classe) {
  return ehSuperficie(classe) ? 'dois-pisos' : 'recorte';
}

/**
 * Prepara a foto no estilo escolhido. Devolve { url, cobre } — `cobre` diz se
 * a imagem deve preencher o quadro inteiro (object-fit: cover) em vez de caber
 * dentro dele.
 */
// `ladoMax`: o pôster A3 pede foto maior que o card do catálogo.
export async function prepararFoto(src, { estilo = 'recorte', ladoMax = LADO_MAX } = {}) {
  const img = await carregarImagem(src);
  // Sempre devolve a imagem em si (data URI). Devolver `src` quando a foto já
  // era pequena mandava o endereço temporário do navegador (blob:...) pro
  // servidor, que recusava — foi o "não consegui gerar o catálogo".
  // Foto com transparência (PNG que a gerente subiu já recortado) continua PNG:
  // virar JPEG pintava o fundo de preto, visível nos blocos sem moldura.
  const jpg = (c) => {
    let tela = c;
    if (c === img) {
      tela = document.createElement('canvas');
      tela.width = img.naturalWidth; tela.height = img.naturalHeight;
      tela.getContext('2d').drawImage(img, 0, 0);
    }
    return temTransparencia(tela) ? tela.toDataURL('image/png') : tela.toDataURL('image/jpeg', 0.78);
  };

  if (estilo === 'original') return { url: jpg(reduzir(img, ladoMax)), cobre: false };
  if (estilo === 'preencher') return { url: jpg(reduzir(img, ladoMax)), cobre: true };
  if (estilo === 'dois-pisos') {
    // Foto de ambiente (sala decorada, banheiro pronto) costuma ser bem mais
    // larga que alta. Duplicar uma sala fica esquisito — sugere preencher, e a
    // tela troca sozinha se a gerente ainda não escolheu o estilo na mão.
    const razao = img.naturalWidth / img.naturalHeight;
    const sugestao = razao > 1.3 ? 'preencher' : null;
    return { url: reduzir(await doisPisos(img), ladoMax).toDataURL('image/png'), cobre: false, sugestao };
  }

  // 'recorte'. Recorta já perto do tamanho de saída: foto de 4000 px levava 3 s
  // com a tela parada, pra depois ser reduzida a 1400 de qualquer jeito.
  const base = reduzir(img, Math.max(ladoMax, LADO_TRABALHO));
  const { canvas, apagados, semFundo, jaRecortada, produtoIA } = await recortarFundo(base, src);
  if (jaRecortada) return { url: reduzir(canvas, ladoMax).toDataURL('image/png'), cobre: false };
  // Recorte que comeu quase tudo, ou foto sem fundo liso: a foto é a própria
  // superfície. Melhor preencher do que mostrar buraco. Mas produto fino (escada,
  // torneira: 85–93% da foto é fundo) virava a foto inteira, um retângulo branco
  // no pôster preto. Se a IA vê produto ali (piso dá ~0%, produto fino 5% ou
  // mais), o recorte vale.
  const limite = produtoIA >= 0.03 ? 0.97 : 0.80;
  if (apagados > limite || semFundo) return { url: jpg(reduzir(img, ladoMax)), cobre: true };
  return { url: reduzir(canvas, ladoMax).toDataURL('image/png'), cobre: false };
}

// Amostra a imagem procurando pixel não opaco (passo largo: só precisa achar um).
function temTransparencia(canvas) {
  try {
    const { width: W, height: H } = canvas;
    const d = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, W, H).data;
    const passo = Math.max(1, Math.floor((W * H) / 40000)) * 4;
    for (let i = 3; i < d.length; i += passo) if (d[i] < 250) return true;
  } catch { /* canvas contaminado: trata como opaca */ }
  return false;
}

/**
 * Prepara a foto que a gerente escolheu no computador pra subir: reduz pra no
 * máximo 1600 px. Foto de celular passa fácil do limite de envio; 1600 px sobra
 * pro card maior do catálogo. PNG continua PNG (pode ter transparência).
 */
export async function reduzirParaEnvio(file) {
  const dataUri = await lerArquivo(file);
  const img = await carregarImagem(dataUri);
  const LADO = 1600;
  const escala = Math.min(1, LADO / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(img.naturalWidth * escala);
  c.height = Math.round(img.naturalHeight * escala);
  const ctx = c.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, c.width, c.height);
  const png = file.type === 'image/png' || temTransparencia(c);
  const saida = png ? c.toDataURL('image/png') : c.toDataURL('image/jpeg', 0.9);
  const [, mime, dados] = /^data:([^;]+);base64,(.*)$/.exec(saida);
  return { mime, dados };
}

/** Lê um arquivo escolhido pela gerente como data URI. */
export function lerArquivo(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Não consegui ler o arquivo.'));
    r.readAsDataURL(file);
  });
}

// Arte do pôster (topo, fundo, fundo completo): aqui o que importa é a QUALIDADE.
// Se já está no tamanho de A3 a 300 dpi e cabe no envio, vai o arquivo original,
// sem recomprimir. Só reduz o que passa disso (largura acima de 3508 px ou mais
// de 18 MB), mantendo PNG quando tem transparência.
const ARTE_LARGURA_MAX = 3508;
const ARTE_MAX_BYTES = 18 * 1024 * 1024;
export async function prepararArte(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file?.type || '')) throw new Error('Use uma imagem JPG, PNG ou WEBP.');
  const url = URL.createObjectURL(file);
  try {
    const img = await carregarImagem(url);
    const W = img.naturalWidth, H = img.naturalHeight;
    if (W <= ARTE_LARGURA_MAX * 1.05 && file.size <= ARTE_MAX_BYTES) {
      const dataUri = await lerArquivo(file);
      return { mime: file.type, dados: dataUri.split(',')[1], largura: W, altura: H };
    }
    const escala = Math.min(1, ARTE_LARGURA_MAX / W);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(W * escala);
    canvas.height = Math.round(H * escala);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const png = temTransparencia(canvas);
    let dataUri = png ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.92);
    if (!png && dataUri.length * 0.75 > ARTE_MAX_BYTES) dataUri = canvas.toDataURL('image/jpeg', 0.85);
    if (dataUri.length * 0.75 > ARTE_MAX_BYTES) throw new Error('A imagem passa de 18 MB mesmo reduzida pra A3. Salve como JPG e tente de novo.');
    return { mime: png ? 'image/png' : 'image/jpeg', dados: dataUri.split(',')[1], largura: canvas.width, altura: canvas.height };
  } finally {
    URL.revokeObjectURL(url);
  }
}
